import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  FileUpload,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Link,
  StatusIndicator,
  Modal,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationSplitListResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useSplitByList } from "../../hooks/useInvestigations";
import { investigationPath } from "./investigationUtils";

interface InvestigationSplitListModalProps {
  stage: Stage;
  investigation: Investigation;
  onClose: () => void;
  /** Prefilled entries (e.g. items ticked in the Items tab or returned items). */
  initialLines?: string[];
  /** What the prefilled entries are, for the suggested title (e.g. "returned items"). */
  itemsLabel?: string;
}

/** Suggested child title: the parent's title plus how many items move, within 200 chars. */
export const suggestSplitTitle = (
  parentTitle: string,
  count: number,
  itemsLabel?: string
): string => {
  const what = itemsLabel ?? (count === 1 ? "listed item" : "listed items");
  const suffix = ` (${count.toLocaleString()} ${what})`;
  return parentTitle.slice(0, 200 - suffix.length).trimEnd() + suffix;
};

/** One entry per line, or comma/semicolon/whitespace separated (DLQ keys never contain
 * spaces); blanks dropped. */
export const listLines = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((s) => s.trim().replace(/^"|"$/g, ""))
    .filter(Boolean);

/**
 * Split by list (R22): paste or upload DLQ keys (not order IDs, which would also move items that
 * arrived for those orders later), check what they match in
 * this investigation, then move those items into a child investigation. For populations no rule can
 * separate (e.g. orders audited as safe to delete). The child never gains items later.
 */
export const InvestigationSplitListModal: React.FC<
  InvestigationSplitListModalProps
> = ({ stage, investigation, onClose, initialLines, itemsLabel }) => {
  const navigate = useNavigate();
  const split = useSplitByList(stage);
  const [text, setText] = useState((initialLines ?? []).join("\n"));
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [comment, setComment] = useState("");
  const [checked, setChecked] = useState<InvestigationSplitListResult | null>(
    null
  );
  const [checkedFor, setCheckedFor] = useState("");
  const lines = listLines(text);
  const stale = checked !== null && checkedFor !== text;
  // Opened from ticked or returned items: the list is already known, so show a count instead of
  // the paste box and check it straight away.
  const prefilled = (initialLines?.length ?? 0) > 0;

  const readFile = async (list: File[]) => {
    setFiles(list);
    if (list[0]) setText(await list[0].text());
  };

  const check = () =>
    split.mutate(
      { investigationId: investigation.id, lines, dryRun: true },
      {
        onSuccess: (result) => {
          setChecked(result);
          setCheckedFor(text);
          if (!titleEdited && result.matchedCount > 0)
            setTitle(
              suggestSplitTitle(
                investigation.title,
                result.matchedCount,
                itemsLabel
              )
            );
        },
      }
    );

  useEffect(() => {
    if (prefilled) check();
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = () =>
    split.mutate(
      {
        investigationId: investigation.id,
        lines,
        title: title.trim(),
        initialComment: comment.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          onClose();
          if (result.childInvestigationId)
            navigate(investigationPath(result.childInvestigationId, stage));
        },
      }
    );

  const canCreate =
    !!checked &&
    !stale &&
    checked.matchedCount > 0 &&
    checked.remainingCount > 0 &&
    !!title.trim();

  return (
    <Modal
      visible
      size="medium"
      onDismiss={onClose}
      header={
        <Header
          variant="h2"
          description={
            prefilled
              ? "Move these items into a new investigation. It never gains new items."
              : "Move a list of items into a new investigation. Use it when no rule can tell the items apart. It never gains new items."
          }
        >
          {prefilled ? "Split into a new investigation" : "Split by list"}
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            {checked && !stale ? (
              <Button
                variant="primary"
                disabled={!canCreate}
                loading={split.isLoading}
                onClick={create}
              >
                Split out investigation
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={lines.length === 0}
                loading={split.isLoading}
                onClick={check}
              >
                Check
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {prefilled ? (
          !checked && (
            <StatusIndicator type={split.isLoading ? "loading" : "pending"}>
              Checking {lines.length.toLocaleString()}{" "}
              {itemsLabel ??
                (lines.length === 1 ? "selected item" : "selected items")}
            </StatusIndicator>
          )
        ) : (
          <>
            <FormField
              label="DLQ keys"
              description="One per line, e.g. copied from the Items tab. Order IDs are not accepted."
            >
              <Textarea
                value={text}
                rows={6}
                placeholder={
                  "523e2328-a87d-44e3-95f8-8093b37ae5f6\n77efd70d-ffef-449c-8771-e13497f37d57"
                }
                onChange={({ detail }) => setText(detail.value)}
                ariaLabel="List of DLQ keys"
              />
            </FormField>
            <FileUpload
              value={files}
              accept=".csv,.txt,text/csv,text/plain"
              onChange={({ detail }) => {
                readFile(detail.value).catch(console.error);
              }}
              i18nStrings={{
                uploadButtonText: () => "Upload a CSV",
                dropzoneText: () => "Drop a CSV to upload",
                removeFileAriaLabel: () => "Remove file",
                limitShowFewer: "Show fewer",
                limitShowMore: "Show more",
                errorIconAriaLabel: "Error",
              }}
            />
          </>
        )}
        {split.error && <Alert type="error">{split.error.message}</Alert>}
        {checked && !stale && (
          <SpaceBetween size="s">
            <KeyValuePairs
              columns={checked.unmatchedCount > 0 ? 3 : 2}
              items={[
                {
                  label: "Move to the new case",
                  value: checked.matchedCount.toLocaleString(),
                },
                {
                  label: "Stay here",
                  value: checked.remainingCount.toLocaleString(),
                },
                ...(checked.unmatchedCount > 0
                  ? [
                      {
                        label: "Not found",
                        value: checked.unmatchedCount.toLocaleString(),
                      },
                    ]
                  : []),
              ]}
            />
            {(checked.heldElsewhere || []).length > 0 && (
              <Box variant="small" color="text-body-secondary">
                Skipped {(checked.heldElsewhereCount ?? 0).toLocaleString()}{" "}
                already in{" "}
                {(checked.heldElsewhere || []).map((o, i) => (
                  <React.Fragment key={o.investigationId}>
                    {i > 0 && ", "}
                    <Link
                      href={investigationPath(o.investigationId, stage)}
                      external
                      fontSize="body-s"
                    >
                      {o.title || o.investigationId}
                    </Link>
                    {(checked.heldElsewhere || []).length > 1 &&
                      ` (${o.count.toLocaleString()})`}
                  </React.Fragment>
                ))}
              </Box>
            )}
            {!!checked.samples?.length && (
              <Box variant="small" color="text-body-secondary">
                Includes orders {checked.samples.join(", ")}
              </Box>
            )}
            {checked.matchedCount > 0 && checked.remainingCount === 0 && (
              <Alert type="warning">
                Every item is on the list. Keep working in this investigation
                instead.
              </Alert>
            )}
            {checked.unmatchedCount > 0 && (
              <ExpandableSection
                headerText={`${checked.unmatchedCount.toLocaleString()} not found in this investigation`}
              >
                <Box variant="code">
                  {(checked.unmatched || []).join("\n")}
                  {checked.unmatchedCount > (checked.unmatched || []).length
                    ? "\n…"
                    : ""}
                </Box>
              </ExpandableSection>
            )}
            {checked.matchedCount > 0 && checked.remainingCount > 0 && (
              <ColumnLayout columns={1}>
                <FormField label="Title of the new investigation">
                  <Input
                    value={title}
                    onChange={({ detail }) => {
                      setTitle(detail.value);
                      setTitleEdited(true);
                    }}
                    ariaLabel="New case title"
                  />
                </FormField>
                <FormField
                  label="First comment"
                  constraintText="Optional. How the list was built and checked."
                >
                  <Textarea
                    value={comment}
                    rows={3}
                    onChange={({ detail }) => setComment(detail.value)}
                    ariaLabel="First comment"
                  />
                </FormField>
              </ColumnLayout>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default InvestigationSplitListModal;
